// ============================================================================
// AdminBudgetPanel — per-event budget editor (UIF only).
// ============================================================================
// Renders inside AdminLayout via #admin/budgets/:calendarEntryId. Loads the
// event (for title display) + budget (lines + event_totals + series_totals)
// and exposes:
//   - Totals card with expected vs actual, expense vs income, net
//   - Series rollup card (if this event is part of a series)
//   - Lines table grouped by Expenses / Income
//   - Add line button → inline form
//   - Edit line → inline expansion with funding split editor
//   - Archive line button per row
//
// Backend: GET/POST/PATCH/DELETE /api/admin/events/:id/budget[/lines[/:lineId]]
// Schema: db/011_event_budget_tables.sql
// ============================================================================

import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, Plus, Pencil, Trash2, Loader2, CheckCircle2, AlertCircle,
  ChevronDown, ChevronUp, RefreshCw, DollarSign, Repeat as RepeatIcon, X,
} from 'lucide-react';
import {
  api,
  type BudgetLineRow,
  type BudgetTotals,
  type BudgetLineCreateInput,
  type FundingSplitEntry,
  type FundingSource,
  type PublicEvent,
} from '../api';

const EXPENSE_CATEGORIES = ['stipend', 'supplies', 'catering', 'personnel', 'marketing', 'venue', 'travel', 'other'];
const INCOME_CATEGORIES = ['fee', 'grant', 'sponsorship', 'other'];
const FUNDING_SOURCES: FundingSource[] = ['LFPP', 'AMP', 'UIF'];

interface Props {
  calendarEntryId: number;
  token: string;
}

interface LineFormState {
  category: string;
  description: string;
  expected_amount: string; // string for input control; parsed on submit
  actual_amount: string;
  is_income: boolean;
  notes: string;
  funding: Record<FundingSource, string>; // string percentages
}

const EMPTY_FORM: LineFormState = {
  category: '',
  description: '',
  expected_amount: '',
  actual_amount: '',
  is_income: false,
  notes: '',
  funding: { LFPP: '', AMP: '', UIF: '' },
};

export function AdminBudgetPanel({ calendarEntryId, token }: Props) {
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [lines, setLines] = useState<BudgetLineRow[]>([]);
  const [eventTotals, setEventTotals] = useState<BudgetTotals | null>(null);
  const [seriesTotals, setSeriesTotals] = useState<BudgetTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // UI state for add / edit
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<LineFormState>(EMPTY_FORM);
  const [editingLineId, setEditingLineId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<LineFormState>(EMPTY_FORM);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [budgetRes, eventsRes] = await Promise.all([
        api.budget(token, calendarEntryId),
        // includeDrafts + includeTemplates so the panel works for any event the
        // admin owns — not just live ones.
        api.events({ includePast: true, includeTemplates: true, includeDrafts: true, token }),
      ]);
      setLines(budgetRes.lines ?? []);
      setEventTotals(budgetRes.event_totals);
      setSeriesTotals(budgetRes.series_totals);
      const ev = (eventsRes.events ?? []).find((e) => e.calendar_entry_id === calendarEntryId);
      setEvent(ev ?? null);
    } catch (err: any) {
      setError(err?.message || 'Could not load budget.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [calendarEntryId]);

  // ----- Save handlers -----
  const buildPayload = (form: LineFormState): BudgetLineCreateInput | null => {
    if (!form.category.trim()) return null;
    const expected = Number(form.expected_amount) || 0;
    const actual = Number(form.actual_amount) || 0;
    if (expected < 0 || actual < 0) return null;
    const funding_split: FundingSplitEntry[] = FUNDING_SOURCES
      .map((src) => ({ funding_source: src, percentage: Number(form.funding[src]) || 0 }))
      .filter((f) => f.percentage > 0);
    return {
      category: form.category.trim(),
      description: form.description.trim() || null,
      expected_amount: expected,
      actual_amount: actual,
      is_income: form.is_income,
      notes: form.notes.trim() || null,
      funding_split,
    };
  };

  const fundingSum = (form: LineFormState) =>
    FUNDING_SOURCES.reduce((s, src) => s + (Number(form.funding[src]) || 0), 0);

  const saveAdd = async () => {
    const payload = buildPayload(addForm);
    if (!payload) { setError('Category and non-negative amounts are required.'); return; }
    const sum = fundingSum(addForm);
    if (sum !== 0 && Math.abs(sum - 100) > 0.01) {
      setError(`Funding split must sum to 100% (got ${sum.toFixed(2)}%). Leave all blank for no funding split.`);
      return;
    }
    setBusy(true); setError('');
    try {
      await api.createBudgetLine(token, calendarEntryId, payload);
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Could not create budget line.');
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (editingLineId == null) return;
    const payload = buildPayload(editForm);
    if (!payload) { setError('Category and non-negative amounts are required.'); return; }
    const sum = fundingSum(editForm);
    if (sum !== 0 && Math.abs(sum - 100) > 0.01) {
      setError(`Funding split must sum to 100% (got ${sum.toFixed(2)}%). Leave all blank for no funding split.`);
      return;
    }
    setBusy(true); setError('');
    try {
      await api.updateBudgetLine(token, calendarEntryId, editingLineId, payload);
      setEditingLineId(null);
      setEditForm(EMPTY_FORM);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Could not update budget line.');
    } finally { setBusy(false); }
  };

  const archiveLine = async (line: BudgetLineRow) => {
    const yes = window.confirm(`Archive "${line.category}${line.description ? ': ' + line.description : ''}"?`);
    if (!yes) return;
    setBusy(true);
    try {
      await api.archiveBudgetLine(token, calendarEntryId, line.line_id);
      await load();
    } catch (err: any) {
      alert(err?.message || 'Could not archive line.');
    } finally { setBusy(false); }
  };

  const startEdit = (line: BudgetLineRow) => {
    setEditingLineId(line.line_id);
    setEditForm({
      category: line.category,
      description: line.description ?? '',
      expected_amount: String(line.expected_amount),
      actual_amount: String(line.actual_amount),
      is_income: line.is_income,
      notes: line.notes ?? '',
      funding: {
        LFPP: line.lfpp_pct > 0 ? String(line.lfpp_pct) : '',
        AMP:  line.amp_pct  > 0 ? String(line.amp_pct)  : '',
        UIF:  line.uif_pct  > 0 ? String(line.uif_pct)  : '',
      },
    });
    setError('');
  };

  // ----- Derived data -----
  const expenseLines = useMemo(() => lines.filter((l) => !l.is_income), [lines]);
  const incomeLines  = useMemo(() => lines.filter((l) =>  l.is_income), [lines]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <a href="#admin/budgets" className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-uif-green-dark mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to budgets list
        </a>
        <h1 className="text-2xl font-bold text-gray-900">Budget</h1>
        {event && (
          <p className="text-sm text-gray-600 mt-0.5">
            {event.title}
            {event.series_label && (
              <span className="ml-2 text-xs text-gray-500 inline-flex items-center gap-1">
                <RepeatIcon className="h-3 w-3" /> {event.series_label}
              </span>
            )}
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
      )}

      {/* Totals cards */}
      {loading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">Loading budget…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TotalsCard title="This event" t={eventTotals} />
            {seriesTotals && (
              <TotalsCard title="Series rollup" t={seriesTotals} subtitle={`${seriesTotals.entry_count ?? 0} events · ${seriesTotals.total_line_count ?? 0} lines`} />
            )}
          </div>

          {/* Lines section */}
          <section className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-sm font-semibold text-uif-green-dark uppercase tracking-wider">Budget lines</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={load}
                  disabled={loading || busy}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={() => { setAddOpen((v) => !v); setAddForm(EMPTY_FORM); setError(''); }}
                  className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark"
                >
                  {addOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {addOpen ? 'Cancel' : 'Add line'}
                </button>
              </div>
            </div>

            {addOpen && (
              <div className="bg-uif-green/5 border border-uif-green/30 rounded-md p-4">
                <LineEditor
                  form={addForm}
                  onChange={setAddForm}
                  onSubmit={saveAdd}
                  onCancel={() => { setAddOpen(false); setAddForm(EMPTY_FORM); setError(''); }}
                  busy={busy}
                  submitLabel="Add line"
                  fundingSum={fundingSum(addForm)}
                />
              </div>
            )}

            {/* Expenses */}
            <LinesGroup
              title="Expenses"
              accent="expense"
              lines={expenseLines}
              editingLineId={editingLineId}
              editForm={editForm}
              setEditForm={setEditForm}
              startEdit={startEdit}
              cancelEdit={() => { setEditingLineId(null); setEditForm(EMPTY_FORM); setError(''); }}
              saveEdit={saveEdit}
              archiveLine={archiveLine}
              busy={busy}
              fundingSum={fundingSum}
            />

            {/* Income */}
            <LinesGroup
              title="Income"
              accent="income"
              lines={incomeLines}
              editingLineId={editingLineId}
              editForm={editForm}
              setEditForm={setEditForm}
              startEdit={startEdit}
              cancelEdit={() => { setEditingLineId(null); setEditForm(EMPTY_FORM); setError(''); }}
              saveEdit={saveEdit}
              archiveLine={archiveLine}
              busy={busy}
              fundingSum={fundingSum}
            />

            {lines.length === 0 && !addOpen && (
              <div className="text-center text-sm text-gray-500 py-8 italic">
                No budget lines yet. Click "Add line" to start tracking expenses or income for this event.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// =========================================================================
// Helpers + sub-components
// =========================================================================

function money(n: number | null | undefined): string {
  if (n == null) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function TotalsCard({ title, t, subtitle }: { title: string; t: BudgetTotals | null; subtitle?: string }) {
  if (!t) return null;
  const netExpectedColor = t.net_expected >= 0 ? 'text-red-700' : 'text-uif-green-dark';
  const netActualColor   = t.net_actual   >= 0 ? 'text-red-700' : 'text-uif-green-dark';
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-uif-green-dark uppercase tracking-wider">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        <DollarSign className="h-4 w-4 text-gray-400" />
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div></div>
        <div className="font-semibold text-gray-600 uppercase tracking-wider text-[10px]">Expected</div>
        <div className="font-semibold text-gray-600 uppercase tracking-wider text-[10px]">Actual</div>

        <div className="text-gray-700">Expense</div>
        <div className="font-mono">{money(t.total_expected_expense)}</div>
        <div className="font-mono">{money(t.total_actual_expense)}</div>

        <div className="text-gray-700">Income</div>
        <div className="font-mono">{money(t.total_expected_income)}</div>
        <div className="font-mono">{money(t.total_actual_income)}</div>

        <div className="text-gray-700 font-semibold pt-2 border-t border-gray-200">Net</div>
        <div className={`font-mono font-semibold pt-2 border-t border-gray-200 ${netExpectedColor}`}>
          {money(t.net_expected)}
        </div>
        <div className={`font-mono font-semibold pt-2 border-t border-gray-200 ${netActualColor}`}>
          {money(t.net_actual)}
        </div>
      </div>
    </div>
  );
}

interface LinesGroupProps {
  title: string;
  accent: 'expense' | 'income';
  lines: BudgetLineRow[];
  editingLineId: number | null;
  editForm: LineFormState;
  setEditForm: (f: LineFormState) => void;
  startEdit: (l: BudgetLineRow) => void;
  cancelEdit: () => void;
  saveEdit: () => void;
  archiveLine: (l: BudgetLineRow) => void;
  busy: boolean;
  fundingSum: (f: LineFormState) => number;
}

function LinesGroup({
  title, accent, lines, editingLineId, editForm, setEditForm, startEdit, cancelEdit, saveEdit, archiveLine, busy, fundingSum,
}: LinesGroupProps) {
  if (lines.length === 0) return null;
  const subtotalExpected = lines.reduce((s, l) => s + Number(l.expected_amount), 0);
  const subtotalActual   = lines.reduce((s, l) => s + Number(l.actual_amount), 0);
  const accentColor = accent === 'expense' ? 'text-red-700' : 'text-uif-green-dark';
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
        <div className={`text-xs font-semibold ${accentColor} uppercase tracking-wider`}>{title}</div>
        <div className="text-xs text-gray-600">
          <span className="mr-3">Expected: <span className="font-mono font-semibold">{money(subtotalExpected)}</span></span>
          <span>Actual: <span className="font-mono font-semibold">{money(subtotalActual)}</span></span>
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-white border-b border-gray-200">
          <tr>
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Category</th>
            <th className="px-3 py-2 text-left font-semibold text-gray-600">Description</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-600">Expected</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-600">Actual</th>
            <th className="px-3 py-2 text-center font-semibold text-gray-600">LFPP</th>
            <th className="px-3 py-2 text-center font-semibold text-gray-600">AMP</th>
            <th className="px-3 py-2 text-center font-semibold text-gray-600">UIF</th>
            <th className="px-3 py-2 text-right font-semibold text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {lines.map((line) => {
            const isEditing = editingLineId === line.line_id;
            return (
              <Fragment key={line.line_id}>
                <tr className={isEditing ? 'bg-uif-green/5' : 'hover:bg-gray-50'}>
                  <td className="px-3 py-2 capitalize">{line.category}</td>
                  <td className="px-3 py-2 text-gray-700">{line.description || <span className="text-gray-400">—</span>}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(Number(line.expected_amount))}</td>
                  <td className="px-3 py-2 text-right font-mono">{money(Number(line.actual_amount))}</td>
                  <td className="px-3 py-2 text-center">{line.lfpp_pct > 0 ? `${line.lfpp_pct}%` : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-center">{line.amp_pct > 0 ? `${line.amp_pct}%` : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-center">{line.uif_pct > 0 ? `${line.uif_pct}%` : <span className="text-gray-300">—</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        type="button"
                        onClick={() => isEditing ? cancelEdit() : startEdit(line)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        {isEditing ? <ChevronUp className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        onClick={() => archiveLine(line)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <Trash2 className="h-3 w-3" /> Archive
                      </button>
                    </div>
                  </td>
                </tr>
                {isEditing && (
                  <tr className="bg-uif-green/5">
                    <td colSpan={8} className="px-4 py-4">
                      <LineEditor
                        form={editForm}
                        onChange={setEditForm}
                        onSubmit={saveEdit}
                        onCancel={cancelEdit}
                        busy={busy}
                        submitLabel="Save changes"
                        fundingSum={fundingSum(editForm)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface LineEditorProps {
  form: LineFormState;
  onChange: (f: LineFormState) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
  submitLabel: string;
  fundingSum: number;
}

function LineEditor({ form, onChange, onSubmit, onCancel, busy, submitLabel, fundingSum }: LineEditorProps) {
  const cats = form.is_income ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  const sumOk = fundingSum === 0 || Math.abs(fundingSum - 100) < 0.01;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            checked={!form.is_income}
            onChange={() => onChange({ ...form, is_income: false, category: '' })}
            className="accent-uif-green"
          />
          Expense
        </label>
        <label className="inline-flex items-center gap-1.5 text-sm">
          <input
            type="radio"
            checked={form.is_income}
            onChange={() => onChange({ ...form, is_income: true, category: '' })}
            className="accent-uif-green"
          />
          Income
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Category *</span>
          <select
            value={form.category}
            onChange={(e) => onChange({ ...form, category: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          >
            <option value="">— pick a category —</option>
            {cats.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Description</span>
          <input
            type="text"
            value={form.description}
            onChange={(e) => onChange({ ...form, description: e.target.value })}
            placeholder="e.g. Catered lunch for 16 participants"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Expected amount</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.expected_amount}
            onChange={(e) => onChange({ ...form, expected_amount: e.target.value })}
            placeholder="0.00"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Actual amount</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={form.actual_amount}
            onChange={(e) => onChange({ ...form, actual_amount: e.target.value })}
            placeholder="0.00"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono"
          />
        </label>
        <label className="block md:col-span-2">
          <span className="block text-xs font-medium text-gray-700 mb-1">Notes</span>
          <input
            type="text"
            value={form.notes}
            onChange={(e) => onChange({ ...form, notes: e.target.value })}
            placeholder="Optional context, invoice numbers, etc."
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
        </label>
      </div>

      {/* Funding split */}
      <div className="bg-white border border-gray-200 rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Funding split</div>
          <div className={
            'text-xs font-mono ' +
            (fundingSum === 0 ? 'text-gray-400' : sumOk ? 'text-uif-green-dark font-semibold' : 'text-red-700 font-semibold')
          }>
            Total: {fundingSum.toFixed(2)}%
            {fundingSum > 0 && !sumOk && <span className="ml-1">(must be 100)</span>}
            {fundingSum === 0 && <span className="ml-1 italic">(no split)</span>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {FUNDING_SOURCES.map((src) => (
            <label key={src} className="block">
              <span className="block text-xs text-gray-600 mb-1">{src}</span>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.funding[src]}
                  onChange={(e) => onChange({ ...form, funding: { ...form.funding, [src]: e.target.value } })}
                  placeholder="0"
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono pr-7"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || !form.category || !sumOk}
          className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
